import { ImageUpload } from "@/shared/components/images/image-upload";
import { UploadImageRequest } from "@/services/backend/qpm/monitors/types/monitor";
import {
  useQpmMonitorDeleteImageMutation,
  useQpmMonitorImageMutation,
  useQpmMonitorShowImageQuery,
} from "@/services/backend/qpm/monitors/api/monitor-api";
import { useCallback, useState } from "react";
import { v4 } from "uuid";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { MonitorView } from "@/services/backend/qpm/monitors/types/monitor-view";
import { ImageReq } from "@/services/backend/qpm/monitors/types/monitor-commands";
import { Loader, Trash2 } from "lucide-react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Button } from "@/shared/components/ui/button";
import { usePersistenceStateUpdateOnTrigger } from "@/shared/lib/persistence-state/persistence-state-info";
import t from "@/lang/lang";

type MonitorImageProps = {
  monitor: MonitorView;
  disabled?: boolean;
  className?: string;
};

export function MonitorImageComponent({
  monitor,
  disabled,
  className,
}: MonitorImageProps) {
  const [request, setRequest] = useState<ImageReq>({
    fileId: "",
    monitorId: monitor.id,
    file: null,
  });

  const {
    data: imageData,
    isLoading,
    error,
  } = useQpmMonitorShowImageQuery(
    {
      fileId: monitor.image?.fileUuid ?? "",
      filePrefix: monitor.image?.prefix ?? "",
    },
    {
      skip: !monitor.image || !monitor.image.fileUuid || !monitor.image.prefix,
    },
  );

  const [
    upload,
    {
      error: errorUpload,
      isLoading: isLoadingUpload,
      isSuccess: isSuccessUpload,
      reset: resetUpload,
    },
  ] = useQpmMonitorImageMutation();
  const [
    deleteImage,
    {
      error: deleteError,
      isLoading: isDeleteLoading,
      isSuccess: isDeleteSuccess,
      reset: resetDelete,
    },
  ] = useQpmMonitorDeleteImageMutation();

  const uploadFile = useCallback(
    (file: File) => {
      if (disabled) return;
      const req: UploadImageRequest = {
        monitorId: monitor.id,
        fileId: v4(),
        file,
      };

      setRequest(req);
    },
    [monitor.id, disabled],
  );

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    upload,
    isLoadingUpload,
    errorUpload,
    isSuccessUpload,
    resetUpload,
    250,
  );

  const { onTrigger: triggerDeleteImage } = usePersistenceStateUpdateOnTrigger(
    { monitorId: monitor.id },
    deleteImage,
    isDeleteLoading,
    deleteError,
    isDeleteSuccess,
    resetDelete,
    { toastError: true, toastSuccess: true },
  );

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center">
        <Loader className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className={className ?? "flex w-full flex-col gap-1.5"}>
      <div className="rounded-sm border p-4">
        <ImageUpload
          id="picture"
          showImagePreview
          existingImageUrl={imageData && imageData.objectURL}
          onUpload={uploadFile}
          err={parseRTKQueryError(errorUpload ?? deleteError)}
          disabled={disabled || isLoadingUpload || isDeleteLoading}
          wrapperClassName="min-h-64"
          imageClassName="max-h-48 object-contain"
          actions={
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={triggerDeleteImage}
                    disabled={
                      disabled ||
                      isLoadingUpload ||
                      isDeleteLoading ||
                      !monitor.image
                    }
                    className="cursor-pointer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Bild löschen")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          }
        />
      </div>
    </div>
  );
}
